import { CENTER_REGION_ID, DEFAULT_EDGE_WRAP_ORDER } from './constants.js';
import { resolveDropTargetNodeId } from './layoutRegions.js';
import {
    bumpEdgeWrapOrder,
    cloneTree,
    detachNode,
    dockGroupAtTarget,
    dockGroupToRegion,
    dockPanelAtTarget,
    dockPanelToCenterRegion,
    dockPanelToRegion,
    rebuildLayoutWithEdgeOrder,
    syncRegionsAfterChange,
} from './layoutTree.js';
import { checkGroupDropInvariants, checkPanelDropInvariants } from './dockInvariants.js';

function isRootCenterHit(hit) {
    return (hit.hostType === 'center' || hit.nodeId === CENTER_REGION_ID) && hit.zone === 'center';
}

export function reduceDockDrop(state, command) {
    const {
        intent,
        dragState,
        panelId,
        panelZoneMap,
    } = command;
    if (!intent?.hit?.zone) return { ...state, ok: false };

    const hit = intent.hit;
    const dragMode = dragState?.dragMode || 'panel';
    const beforeRoot = cloneTree(state.root);
    let root = beforeRoot;
    let regions = { ...(state.regions || {}) };
    let panels = { ...(state.panels || {}) };
    let edgeWrapOrder = [...(state.edgeWrapOrder || DEFAULT_EDGE_WRAP_ORDER)];
    let ok = false;

    if (dragMode === 'group') {
        const groupNodeId = dragState.groupNodeId;
        const detachedResult = detachNode(root, groupNodeId);
        root = detachedResult.tree;
        const subtree = detachedResult.detached;
        if (!subtree) {
            return { ...state, ok: false };
        }

        if (hit.tier === 'root' && hit.region) {
            const result = dockGroupToRegion(
                root,
                regions,
                subtree,
                hit.region,
                panelZoneMap,
            );
            root = result.root;
            regions = result.regions;
        } else if (isRootCenterHit(hit)) {
            const result = dockGroupToRegion(
                root,
                regions,
                subtree,
                'center',
                panelZoneMap,
            );
            root = result.root;
            regions = result.regions;
        } else if (hit.nodeId) {
            const result = dockGroupAtTarget(
                root,
                regions,
                subtree,
                hit.nodeId,
                hit.zone,
            );
            root = result.root;
            regions = result.regions;
        }

        const invariant = checkGroupDropInvariants(root, subtree);
        ok = invariant.ok;
        if (!ok) {
            root = beforeRoot;
            regions = syncRegionsAfterChange(root, panelZoneMap);
        }
    } else {
        if (!panelId) return { ...state, ok: false };

        if (hit.tier === 'root' && hit.region) {
            const result = dockPanelToRegion(
                beforeRoot,
                regions,
                panelId,
                hit.region,
                undefined,
                panelZoneMap,
            );
            root = result.root;
            regions = syncRegionsAfterChange(root, panelZoneMap);
        } else if (isRootCenterHit(hit)) {
            const result = dockPanelToCenterRegion(
                beforeRoot,
                regions,
                panelId,
                panelZoneMap,
            );
            root = result.root;
            regions = result.regions;
        } else {
            const targetNodeId = resolveDropTargetNodeId(beforeRoot, hit);
            if (!targetNodeId) {
                ok = false;
            } else {
                root = dockPanelAtTarget(
                    beforeRoot,
                    panelId,
                    targetNodeId,
                    hit.zone,
                );
                regions = syncRegionsAfterChange(root, panelZoneMap);
            }
        }

        const invariant = checkPanelDropInvariants(beforeRoot, root, panelId);
        ok = invariant.ok;
        if (!ok) {
            root = beforeRoot;
            regions = syncRegionsAfterChange(root, panelZoneMap);
        } else {
            panels[panelId] = {
                ...(panels[panelId] || {}),
                lastDock: {
                    tier: hit.tier,
                    region: hit.region,
                    nodeId: hit.nodeId,
                    zone: hit.zone,
                },
                ...(hit.tier === 'root' && hit.region
                    ? { layoutRegion: hit.region }
                    : {}),
            };
        }
    }

    if (
        ok &&
        hit.tier === 'root' &&
        hit.region &&
        hit.region !== 'center'
    ) {
        edgeWrapOrder = bumpEdgeWrapOrder(edgeWrapOrder, hit.region);
        const zoneMapForRebuild = { ...panelZoneMap };
        if (dragMode === 'panel' && panelId) {
            zoneMapForRebuild[panelId] = hit.region;
        }
        const rebuilt = rebuildLayoutWithEdgeOrder(
            root,
            regions,
            zoneMapForRebuild,
            edgeWrapOrder,
        );
        root = rebuilt.root;
        regions = syncRegionsAfterChange(root, zoneMapForRebuild);
    }

    return {
        ...state,
        root,
        regions,
        panels,
        edgeWrapOrder,
        ok,
    };
}

